import React, { useEffect, useRef, useState, useImperativeHandle, useMemo } from "react";
import attachImage from "src/img/new/attach-image.svg";
import { TextLabel } from "../style";
import { VideoBottomControls, VideoContainer, 
    VideoControlsBar, ControlButton, 
    CustomRange, PauseIcon, 
    PlayIcon, formatTime,
    VolumeIcon, FullscreenIcon
} from "src/components";
import styled from "styled-components";
import { useVideoPlayer } from "../../hooks/VideoPreview";

const getMediaBoxWidth = (boxStyle) => boxStyle?.width || "332px";
const getMediaBoxHeight = (boxStyle) => boxStyle?.height || "186px";
const getMediaBoxAspectRatio = (boxStyle) => boxStyle?.aspectRatio || "16 / 9";

export const ThumbnailPlaceholderPreview = styled.div`
    width: ${({ boxStyle }) => getMediaBoxWidth(boxStyle)};
    height: ${({ boxStyle }) => getMediaBoxHeight(boxStyle)};
    max-width: 100%;
    aspect-ratio: ${({ boxStyle }) => getMediaBoxAspectRatio(boxStyle)};
    box-sizing: border-box;
    border-radius: ${({ boxStyle }) => boxStyle?.borderRadius || "12px"};
    border: 1px solid #D8DEEC;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0px 5px 5px #00000026;

    img {
        max-width: 100%;
        max-height: 100%;
    }
`;

export const ImagePreview = styled.img`
    width: 332px;
    height: 186px;
    max-width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    border: 1px solid #8291b2;
    object-fit: contain;
    box-shadow: 0px 5px 5px #00000026;
`;

const UploadMedia = ({
    isCircleDisplay,
    onFileSelect,
    file,
    isVideo,
    allFileType = false,
    boxStyle,
    buttonStyle,
    fileType = "image",
    croppedFile,
    setFileType = () => { },
    cropperResults,
    hideBrowseButton,
}, ref) => {
    const [preview, setPreview] = useState(croppedFile || null);
    const fileInputRef = useRef(null);
    const [error, setError] = useState("");
    const {
        videoRef,
        containerRef,
        muted,
        currentTime,
        duration,
        isPlaying,
        isHovered,
        setIsHovered,
        cropStyles,
        useCrop,
        togglePlayPause,
        toggleMute,
        toggleFullscreen,
        handleProgressChange,
        progressValue,
        validTrimStart
    } = useVideoPlayer({
        videoSrc: preview,
        trimTimeStart: cropperResults?.trimStart,
        trimTimeEnd: cropperResults?.trimEnd,
        crop: cropperResults?.crop,
    });


    const isImage = preview && fileType === "image";
    const isVid = preview && fileType === "video";

    useImperativeHandle(ref, () => ({
        triggerFilePicker: () => {
            fileInputRef.current?.click();
        },
    }));

    // Handle file input change
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const type = file.type.split("/")[0];
            if (type === "image" || type === "video") {
                setFileType(type);
                setPreview(URL.createObjectURL(file));
                setError("");

                // Read as data URL and pass to parent callback
                const reader = new FileReader();
                reader.onload = () => {
                    onFileSelect(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setError("Only image or video files are allowed.");
            }
        }
    };

    useEffect(() => {
        if (file) setPreview(file);
    }, [file]);

    useEffect(() => {
        if (croppedFile) {
            setPreview(croppedFile);
        } else if (!file) {
            setPreview(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = null;
            }
        }
    }, [croppedFile, file]);

    const handleClickInput = () => {
        fileInputRef.current?.click();
    };

    const withSafeClick = (handler) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        handler?.(e);
    };

    const stableCrop = useMemo(() => cropperResults?.crop, [JSON.stringify(cropperResults?.crop)]);
    return (
        <div
            className="upload-media"
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                width: boxStyle?.containerWidth || "fit-content",
                maxWidth: "100%",
            }}
        >
            {isImage ? (
                <ImagePreview
                    src={croppedFile ?? preview}
                    alt="attach_image"
                    style={{
                        width: boxStyle?.width ?? "332px",
                        height: boxStyle?.height ?? "186px",
                        maxWidth: "100%",
                        aspectRatio: boxStyle?.aspectRatio ?? "16 / 9",
                        borderRadius: isCircleDisplay ? "100px" : "12px",
                        objectFit: "cover",
                    }}
                />
            ) : isVid ? (
                <VideoContainer
                    ref={containerRef}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    style={{
                        width: boxStyle?.width ?? "332px",
                        height: boxStyle?.height ?? "186px",
                        maxWidth: "100%",
                        aspectRatio: boxStyle?.aspectRatio ?? "16 / 9",
                        borderRadius: boxStyle?.borderRadius ?? "12px",
                        border: "1px solid #D8DEEC",
                        boxShadow: "0px 5px 5px #00000026",
                        overflow: useCrop ? "hidden" : "visible",
                        position: "relative",
                        cursor: isPlaying && !isHovered ? "none" : "default",
                    }}
                >
                    {stableCrop ? (
                        <>
                            <div
                                style={{
                                    width: boxStyle?.width ?? "332px",
                                    height: boxStyle?.height ?? "186px",
                                    maxWidth: "100%",
                                    aspectRatio: boxStyle?.aspectRatio ?? "16 / 9",
                                    borderRadius: boxStyle?.borderRadius ?? "12px",
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                            >
                                <video
                                    ref={videoRef}
                                    muted={muted}
                                    controls={false}
                                    style={cropStyles}
                                >
                                    <source src={preview} type="video/mp4" /> Your browser does not support the video tag.
                                </video>
                            </div>

                            <VideoBottomControls show={!isPlaying || isHovered} >
                                <VideoControlsBar interactive >
                                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                        <ControlButton type="button" onClick={withSafeClick(togglePlayPause)}>
                                            {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                        </ControlButton>
                                        <span 
                                            style={{
                                                fontSize: "0.85rem",
                                                fontFamily: "Manrope, sans-serif",
                                            }}
                                        >
                                            {formatTime(Math.max(0, currentTime - validTrimStart))} / {formatTime(duration)}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <ControlButton type="button" onClick={withSafeClick(toggleMute)}>
                                            <VolumeIcon muted={muted} />
                                        </ControlButton>
                                        <ControlButton type="button" onClick={withSafeClick(toggleFullscreen)}>
                                            <FullscreenIcon />
                                        </ControlButton>
                                    </div>
                                </VideoControlsBar>
                                <CustomRange
                                    value={progressValue}
                                    max={duration}
                                    min={0}
                                    step="0.1"
                                    onChange={handleProgressChange}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </VideoBottomControls>
                        </>
                    ) : (
                        <video
                            ref={videoRef}
                            muted={muted}
                            controls
                            style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: boxStyle?.borderRadius ?? "12px",
                                objectFit: "cover",
                            }}
                        >
                            <source src={preview} type="video/mp4" />Your browser does not support the video tag.
                        </video>
                    )}
                </VideoContainer>
            ) : (
                <ThumbnailPlaceholderPreview boxStyle={boxStyle}>
                    <img src={attachImage} alt="attach_image" />
                </ThumbnailPlaceholderPreview>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={allFileType ? "image/*,video/*" : isVideo ? "video/*" : "image/*"}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
            <TextLabel>{error}</TextLabel>

            {!hideBrowseButton && (
                <button
                    onClick={handleClickInput}
                    type="button"
                    style={{
                        width: "140px",
                        height: "38px",
                        backgroundColor: "#000000",
                        color: "#FFFFFF",
                        borderRadius: buttonStyle?.borderRadius ?? "8px",
                        font: "normal normal 700 14px/16px Manrope",
                        alignSelf: buttonStyle?.alignSelf ?? "flex-start",
                        cursor: "pointer",
                        border: buttonStyle?.border ?? "1px solid #000000",
                        boxShadow: buttonStyle?.boxShadow ?? "none",
                        ...buttonStyle,
                    }}
                > Browse File
                </button>
            )}
        </div>
    );
};

export default React.forwardRef(UploadMedia);
